import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Award, Download } from 'lucide-react';
import { publicCertificateFileUrl, useApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui';
import { LinkedInActions } from '@/components/certificate-download';
import { formatCertificateDate, formatDateTime } from '@/lib/dates';

const secondaryLink =
  'flex items-center justify-center gap-2 rounded-[var(--radius)] border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted';

/**
 * Public certificate verification, for anyone checking a certificate. Visitors
 * can download a copy for their records. The signed-in owner also gets the
 * LinkedIn actions (as on Your certificates); nobody else does, so no one
 * can add another person's certificate to their profile.
 */
export function VerifyCertificatePage() {
  const { code = '' } = useParams();
  const api = useApi();
  const cert = useQuery({
    queryKey: ['verify', code],
    queryFn: () => api.verifyCertificate(code),
    retry: false,
  });
  const { user } = useAuth();
  const mine = useQuery({
    queryKey: ['certificates'],
    queryFn: api.certificates,
    enabled: !!user,
  });
  const isOwner = !!mine.data?.certificates.some((m) => m.code === code.toUpperCase());

  if (cert.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (cert.isError) {
    return (
      <Card className="mx-auto max-w-md text-center">
        <p className="font-medium">Certificate not found</p>
        <p className="mt-1 text-sm text-muted-foreground">
          It may be private, or the code may be wrong.
        </p>
      </Card>
    );
  }

  const c = cert.data;

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <Card className="text-center">
        <Award className="mx-auto size-10 text-primary" aria-hidden />
        <p className="mt-3 text-sm uppercase tracking-wide text-muted-foreground">
          Certificate of achievement
        </p>
        <h1 className="mt-2 text-2xl font-semibold">{c.title}</h1>
        <p className="mt-4">
          Awarded to <span className="font-semibold">{c.recipientName}</span>
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          by {c.issuerName} · {formatCertificateDate(c.issuedAt, c.timeZone)}
        </p>
        <p className="mt-4 text-xs text-muted-foreground">
          Verified by Nirvik Ready · ID {c.code} · Issued {formatDateTime(c.issuedAt)} your time
        </p>
      </Card>
      {isOwner && <LinkedInActions cert={c} />}
      <div className="grid gap-3 sm:grid-cols-2">
        <a href={publicCertificateFileUrl(c.code, 'pdf')} download className={secondaryLink}>
          <Download className="size-4" aria-hidden /> Download PDF
        </a>
        <a href={publicCertificateFileUrl(c.code, 'png')} download className={secondaryLink}>
          <Download className="size-4" aria-hidden /> Download PNG
        </a>
      </div>
    </div>
  );
}
